import { useMemo } from 'react';

import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useDevelopmentContext } from '@/context/DevelopmentContextProvider';
import { makeUniqueFilter } from '@/util/array';
import { useAdminProductionEnvironments } from './admin/environments';
import { useJobs } from './admin/jobs';
import { useProjects } from './admin/project';
import {
  EnvironmentSummary,
  useEnvironmentSummaries,
} from './discovery/environmentSummary';

export type ProjectEntry = {
  name?: string | null;
  dbtCloudProject?: string;
  dbtCoreProject?: string | null;
  projectId: number;
  defaultEnvironmentId?: number | null;
  projectSummary?: {
    publicModels: number | undefined;
    totalModels: number | undefined;
    metrics: number | undefined;
    exposures: number | undefined;
    groups: number | undefined;
    macros: number | undefined;
    models: number | undefined;
    savedQuerys: number | undefined;
    seeds: number | undefined;
    semanticModels: number | undefined;
    snapshots: number | undefined;
    sources: number | undefined;
    tests: number | undefined;
  };
  hasAccess: boolean;
};

/**
 * Fetches all dbt Cloud projects in the account.
 * This combines data from the Admin API (all projects the user has access to) and Discovery API (all projects in the account with runs)
 * to provide a complete view of all projects in the account.
 */
export const useAllProjects = (accountId?: number) => {
  const devData = useDevelopmentContext();
  const { hasProjectAccess } = useDbtCloudAccount();

  const projects = useProjects({ accountId });
  const jobs = useJobs({ accountId });

  const projectsWithJobRuns = jobs.data?.reduce((acc, job) => {
    if (job.most_recent_completed_run) {
      acc.push(job.project_id);
    }
    return acc;
  }, [] as number[]);

  const defaultEnvs = useAdminProductionEnvironments(
    accountId || 0,
    projects.data?.map((project) => project.id || 0) || [],
  );

  const envList = defaultEnvs.data?.map((env) => env.id!);

  const projectIdToEnvIdMap = useMemo(() => {
    return (
      defaultEnvs.data?.reduce(
        (acc, env) => {
          if (projectsWithJobRuns?.includes(env.project_id!)) {
            acc[env.project_id!] = env.id!;
          }
          return acc;
        },
        {} as Record<number, number>,
      ) || {}
    );
  }, [defaultEnvs.data, projectsWithJobRuns]);

  const environmentSummariesResult = useEnvironmentSummaries({
    accountId: accountId || undefined,
    environmentIds: envList,
  });

  const environmentSummaries = environmentSummariesResult.data?.reduce(
    (acc, summary) => {
      acc[summary.environment_id] = summary;
      return acc;
    },
    {} as Record<number, EnvironmentSummary>,
  );

  const allProjects = useMemo((): ProjectEntry[] => {
    if (devData.shouldMockProjects) {
      return devData.mockProjects.map((project) => ({
        dbtCloudProject: project.name,
        projectId: project.projectId || 0,
        defaultEnvironmentId: project.productionEnvironmentId || 0,
        dbtCoreProject: null,
        hasAccess: true,
        projectSummary: {
          publicModels: 0,
          totalModels: 0,
          metrics: 0,
          exposures: 0,
          snapshots: 0,
          seeds: 0,
          tests: 0,
          sources: 0,
          groups: 0,
          macros: 0,
          models: 0,
          savedQuerys: 0,
          semanticModels: 0,
        },
      }));
    }

    if (!projects.data?.length) {
      return [];
    }

    if (!environmentSummaries) {
      return projects.data.map((project) => ({
        dbtCloudProject: project.name,
        projectId: project.id || 0,
        defaultEnvironmentId: projectIdToEnvIdMap[project.id || 0],
        dbtCoreProject: null,
        hasAccess: hasProjectAccess(project.id || 0),
        projectSummary: {
          publicModels: 0,
          totalModels: 0,
          metrics: 0,
          exposures: 0,
          snapshots: 0,
          seeds: 0,
          tests: 0,
          sources: 0,
          groups: 0,
          macros: 0,
          models: 0,
          savedQuerys: 0,
          semanticModels: 0,
        },
      }));
    }

    return projects.data.map((project) => {
      const projectSummary = (environmentSummaries ?? {})[
        projectIdToEnvIdMap[project.id || 0]
      ];
      return {
        dbtCloudProject: project.name,
        projectId: project.id || 0,
        defaultEnvironmentId: projectIdToEnvIdMap[project.id || 0],
        dbtCoreProject: projectSummary?.dbt_project_name,
        hasAccess: hasProjectAccess(project.id || 0),
        projectSummary: {
          publicModels: projectSummary ? projectSummary.public_model : 0,
          totalModels: projectSummary ? projectSummary.model : 0,
          metrics: projectSummary ? projectSummary.metric : 0,
          exposures: projectSummary ? projectSummary.exposure : 0,
          snapshots: projectSummary ? projectSummary.snapshot : 0,
          seeds: projectSummary ? projectSummary.seed : 0,
          tests: projectSummary ? projectSummary.test : 0,
          sources: projectSummary ? projectSummary.source : 0,
          groups: projectSummary ? projectSummary.groups : 0,
          macros: projectSummary ? projectSummary.macro : 0,
          models: projectSummary ? projectSummary.model : 0,
          savedQuerys: projectSummary ? projectSummary.saved_query : 0,
          semanticModels: projectSummary ? projectSummary.semantic_model : 0,
        },
      } satisfies ProjectEntry;
    });
  }, [
    devData.shouldMockProjects,
    devData.mockProjects,
    projects.data,
    environmentSummaries,
    projectIdToEnvIdMap,
    hasProjectAccess,
  ])
    ?.filter(makeUniqueFilter((project) => project.projectId))
    .filter((project) => project.dbtCloudProject || project.dbtCoreProject);

  return {
    data: allProjects,
    isLoading:
      projects.isFetching ||
      defaultEnvs.isFetching ||
      environmentSummariesResult.fetching,
    isFetching:
      projects.isFetching ||
      defaultEnvs.isFetching ||
      environmentSummariesResult.fetching,
  };
};
